import { campaignSearchRows } from './campaign-search-rows';
import { searchDefinitionPages } from '../janus-engine/authoring/search-definition-results';
import { CREATURE_FILTER_CONTROLS, readCreatureFilterSelection, runCreatureFilter } from './creature-filter';

// filter-search-init.ts — SPELL / ABILITY / INVENTORY FILTER + SEARCH FAMILY (+ catalog/library search),
// carved out of template.js and TS-ported for Phase 2a of plans/bundler-script-load-order-phase2.md — a
// member of the post-template-forms aggregate (footer ~820, between fantasy-cipher and turn-widget singleton).
//
// MODULE-SCOPE work at eval: FOUR run-once-guarded IIFEs that install document-level delegated listeners
// (input / shown.bs.tab / click / show.bs.collapse / hide.bs.collapse) — pure DOM wiring, each guarded by a
// `window.__…` flag, no template.js module-scope reads → clean 2a (same shape as locations' input listener).
// Deps (debounce (dom-utils), getElementValue/BASE_URL (core-helpers), queryData/ftDebugLog (template.js),
// CSS) are reached only inside handlers.
//
// PUBLIC SURFACE (window-attached): the documented 16 exports PLUS fateTideScrubInventoryUnequippedSearchInputsInContainer
// — that last one was an IMPLICIT global (top-level fn) consumed CROSS-FILE by trade-trainer-init.js (bare
// `typeof … === 'function'` guard, 2 sites); it must stay attached or that scrub silently no-ops. The other
// non-exported helpers (fateTideHideEmptyCastableSections / *InWrap / ftDynamicSelectPairRoot / …) have no
// cross-file consumer (grep-verified — only comment mentions) and stay module-private.
//
// Legacy markup can move between native-select, Tom Select, input, and button roles. Narrow those roles
// at each boundary rather than assuming one shared element shape.

interface FtFilterTomSelectOption {
    value?: unknown;
    [key: string]: unknown;
}

interface FtFilterTomSelect {
    getValue(): unknown;
    options: Record<string, FtFilterTomSelectOption>;
    items: string[];
    lastQuery: unknown;
    loadedSearches: Record<string, unknown>;
    clearOptions?(): void;
    removeOption(value: string): void;
    addOption(option: FtFilterTomSelectOption): void;
    addItem(value: string, silent?: boolean): void;
    refreshOptions(triggerDropdown?: boolean): void;
    load?(query: string): void;
}

type FtFilterSelectElement = HTMLSelectElement & { tomselect?: FtFilterTomSelect };
type FtFilterHostElement = HTMLElement & { value?: string; tomselect?: FtFilterTomSelect };

declare global {
    interface Window {
        __ftInvTypeFilterDelegationBound?: boolean;
        __fateTideSpellSearchInputBound?: boolean;
        _ftDeferTabTomSelectWarmupInstalled?: boolean;
        __ftCatalogPtChipFilterDelegation?: boolean;
        __ftInlineSearchDelegationBound?: boolean;
        fateTideApplyCastableSpellFilters: typeof fateTideApplyCastableSpellFilters;
        fateTideApplyPreparedSpellFilters: typeof fateTideApplyPreparedSpellFilters;
        fateTideSetCastableSpellFilter: (mode: unknown, level: unknown, wrapId: unknown) => void;
        fateTideSetPreparedSpellFilter: (mode: unknown, level: unknown) => void;
        fateTideRefreshSpellTableFilters: typeof fateTideRefreshSpellTableFilters;
        fateTideRefreshInventoryUnequippedSearch: typeof fateTideRefreshInventoryUnequippedSearch;
        fateTideBuildInventoryTypeFilterBar: typeof fateTideBuildInventoryTypeFilterBar;
        ftResolveDynamicSelectFilterValue: typeof ftResolveDynamicSelectFilterValue;
        ftRefreshDynamicFilteredSelects: typeof ftRefreshDynamicFilteredSelects;
        fateTideScrubInventoryUnequippedSearchInputsInContainer: typeof fateTideScrubInventoryUnequippedSearchInputsInContainer;
        ftGetProductTypeIdsForLibrarySearch: typeof ftGetProductTypeIdsForLibrarySearch;
        ftEscapeForCssAttributeSelectorSegment: typeof ftEscapeForCssAttributeSelectorSegment;
        ftCatalogSourceListSyncProductTypesUrl: typeof ftCatalogSourceListSyncProductTypesUrl;
        ftInvokeCatalogProductTypeFilterFromRoot: typeof ftInvokeCatalogProductTypeFilterFromRoot;
        searchWithDelay: typeof searchWithDelay;
        ftSyncInlineSearchTrailing: typeof ftSyncInlineSearchTrailing;
        ftInvokeSearchWithDelayFromRoot: typeof ftInvokeSearchWithDelayFromRoot;
        installFtInlineSearchTrailingSyncAll: typeof installFtInlineSearchTrailingSyncAll;
        ftFilterEncounterCreatures: typeof ftFilterEncounterCreatures;
        iconMap?: (id: string, width?: number, height?: number, title?: string | boolean) => string;
        openItemInfo?: (id: string) => unknown;
        addEncounterCreaturesToEncounter?: (id: string, name?: string, look?: { sizeId?: string; pictureUrl?: string }) => unknown;
        addEncounterInventory?: (event: Event, placement?: { x: number; y: number; z: number } | null) => Promise<unknown> | unknown;
        ftMountThumbnails?: (root?: Element | Document) => void;
        ftAdjustForCaster?: (
            url: string,
            casterId: string,
            inputs: Record<string, string>,
            responseTarget: string,
        ) => Promise<unknown>;
        __ftAddInventorySearchBound?: boolean;
    }
}

// =====================================================================================
// FILTER / SEARCH FAMILY
// =====================================================================================
function fateTideHideEmptyCastableSections(wrap: HTMLElement): void {
    wrap.querySelectorAll<HTMLElement>('.spells_known_div').forEach(function (div) {
        var rows = div.querySelectorAll<HTMLElement>('.spells_known_tr');
        if (!rows.length) {
            return;
        }
        var anyShown = Array.from(rows).some(function (tr) {
            return getComputedStyle(tr).display !== 'none';
        });
        div.style.display = anyShown ? '' : 'none';
    });
}

function fateTideApplyCastableSpellFiltersInWrap(wrap: Element): void {
    if (!wrap || !wrap.classList || !wrap.classList.contains('fate-tide-castable-spells-root')) {
        return;
    }
    var mode = wrap.getAttribute('data-filter-mode') || 'all';
    var level = wrap.getAttribute('data-filter-level') || '';
    var input =
        wrap.querySelector<HTMLInputElement>('input.fate-tide-castable-spell-search[data-ft-castable-root="' + wrap.id + '"]') ||
        wrap.querySelector<HTMLInputElement>('input[type="search"]');
    var q = (input && input.value ? input.value : '').toLowerCase().trim();

    if (mode === 'all') {
        wrap.querySelectorAll<HTMLElement>('.spells_known_div').forEach(function (el) {
            el.style.display = '';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr').forEach(function (el) {
            el.style.display = '';
        });
    } else if (mode === 'level') {
        wrap.querySelectorAll<HTMLElement>('.spells_known_div').forEach(function (el) {
            el.style.display = 'none';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_div_' + level).forEach(function (el) {
            el.style.display = '';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr').forEach(function (el) {
            el.style.display = 'none';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr_' + level).forEach(function (el) {
            el.style.display = '';
        });
    } else if (mode === 'concentration') {
        wrap.querySelectorAll<HTMLElement>('.spells_known_div').forEach(function (el) {
            el.style.display = '';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr').forEach(function (el) {
            el.style.display = 'none';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr_concentration').forEach(function (el) {
            el.style.display = '';
        });
    } else if (mode === 'ritual') {
        wrap.querySelectorAll<HTMLElement>('.spells_known_div').forEach(function (el) {
            el.style.display = '';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr').forEach(function (el) {
            el.style.display = 'none';
        });
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr_ritual').forEach(function (el) {
            el.style.display = '';
        });
    }

    if (q) {
        wrap.querySelectorAll<HTMLElement>('.spells_known_tr').forEach(function (tr) {
            if (getComputedStyle(tr).display === 'none') {
                return;
            }
            var name = tr.getAttribute('data-spell-name') || '';
            if (name.indexOf(q) === -1) {
                tr.style.display = 'none';
            }
        });
    }

    fateTideHideEmptyCastableSections(wrap as HTMLElement);
}

function fateTideApplyCastableSpellFilters(): void {
    document.querySelectorAll<HTMLElement>('.fate-tide-castable-spells-root').forEach(fateTideApplyCastableSpellFiltersInWrap);
}

function fateTideApplyPreparedSpellFilters(): void {
    var wrap = document.getElementById('wrap_prepared_spells_table');
    if (!wrap) {
        return;
    }
    var mode = wrap.getAttribute('data-filter-mode') || 'all';
    var level = wrap.getAttribute('data-filter-level') || '';
    var input = document.getElementById('input_prepared_spells_search') as HTMLInputElement | null;
    var q = (input && input.value ? input.value : '').toLowerCase().trim();
    wrap.querySelectorAll<HTMLElement>('.spells_prepared_tr').forEach(function (tr) {
        var name = tr.getAttribute('data-spell-name') || '';
        var matchQ = !q || name.indexOf(q) !== -1;
        var matchMode = true;
        if (mode === 'all') {
            matchMode = true;
        } else if (mode === 'level') {
            matchMode = tr.classList.contains('spells_prepared_tr_' + level);
        } else if (mode === 'concentration') {
            matchMode = tr.classList.contains('spells_prepared_tr_concentration');
        } else if (mode === 'ritual') {
            matchMode = tr.classList.contains('spells_prepared_tr_ritual');
        }
        tr.style.display = matchQ && matchMode ? '' : 'none';
    });
}

window.fateTideApplyCastableSpellFilters = fateTideApplyCastableSpellFilters;
window.fateTideApplyPreparedSpellFilters = fateTideApplyPreparedSpellFilters;

window.fateTideSetCastableSpellFilter = function (mode: unknown, level: unknown, wrapId: unknown) {
    const modeValue = String(mode ?? 'all');
    const wrapIdValue = String(wrapId ?? '').trim();
    var w = wrapIdValue ? document.getElementById(wrapIdValue) : document.querySelector<HTMLElement>('.fate-tide-castable-spells-root');
    if (!w) {
        return;
    }
    w.setAttribute('data-filter-mode', modeValue);
    if (modeValue === 'level' && level !== undefined && level !== null) {
        w.setAttribute('data-filter-level', String(level));
    } else {
        w.setAttribute('data-filter-level', '');
    }
    // Move the active highlight to the chosen pill.
    var pills = w.querySelectorAll<HTMLElement>('[data-ft-spell-filter-mode]');
    pills.forEach(function (btn) {
        var bm = btn.getAttribute('data-ft-spell-filter-mode');
        var bl = btn.getAttribute('data-ft-spell-filter-level');
        var match = bm === modeValue && (modeValue !== 'level' || bl === String(level));
        if (match) { btn.classList.add('active'); } else { btn.classList.remove('active'); }
    });
    fateTideApplyCastableSpellFiltersInWrap(w);
};

window.fateTideSetPreparedSpellFilter = function (mode: unknown, level: unknown) {
    var wrap = document.getElementById('wrap_prepared_spells_table');
    if (!wrap) {
        return;
    }
    const modeValue = String(mode ?? 'all');
    wrap.setAttribute('data-filter-mode', modeValue);
    if (modeValue === 'level' && level !== undefined && level !== null) {
        wrap.setAttribute('data-filter-level', String(level));
    } else {
        wrap.setAttribute('data-filter-level', '');
    }
    fateTideApplyPreparedSpellFilters();
};

function fateTideRefreshSpellTableFilters(): void {
    if (document.getElementById('wrap_prepared_spells_table')) {
        fateTideApplyPreparedSpellFilters();
    }
    document.querySelectorAll<HTMLElement>('.fate-tide-castable-spells-root').forEach(fateTideApplyCastableSpellFiltersInWrap);
    document.querySelectorAll<HTMLElement>('.fate-tide-sheet-abilities-search-root').forEach(fateTideApplySheetAbilitySearchInWrap);
    fateTideRefreshInventoryUnequippedSearch();
}

function fateTideApplySheetAbilitySearchInWrap(wrap: Element): void {
    if (!wrap || !wrap.classList || !wrap.classList.contains('fate-tide-sheet-abilities-search-root')) {
        return;
    }
    var input =
        wrap.querySelector<HTMLInputElement>('input.fate-tide-sheet-ability-search[data-ft-sheet-abilities-root="' + wrap.id + '"]') ||
        wrap.querySelector<HTMLInputElement>('input.fate-tide-sheet-ability-search');
    var q = (input && input.value ? input.value : '').toLowerCase().trim();

    wrap.querySelectorAll<HTMLElement>('.fate-tide-sheet-ability-row').forEach(function (row) {
        if (!q) {
            row.style.display = '';
            return;
        }
        var name = row.getAttribute('data-ft-ability-name') || '';
        row.style.display = name.indexOf(q) !== -1 ? '' : 'none';
    });

    wrap.querySelectorAll<HTMLUListElement>('ul.list-group').forEach(function (ul) {
        var hideFollowers = false;
        ul.querySelectorAll<HTMLLIElement>(':scope > li').forEach(function (li) {
            if (li.getAttribute('aria-current') === 'true') {
                hideFollowers = false;
                return;
            }
            if (li.classList.contains('fate-tide-sheet-ability-row')) {
                hideFollowers = getComputedStyle(li).display === 'none';
                return;
            }
            if (li.classList.contains('fate-tide-sheet-ability-subrow')) {
                li.style.display = !q || !hideFollowers ? '' : 'none';
                return;
            }
            li.style.display = '';
        });
    });

    wrap.querySelectorAll<HTMLElement>('.fate-tide-sheet-ability-section').forEach(function (sec) {
        if (!q) {
            sec.style.display = '';
            return;
        }
        var rows = sec.querySelectorAll<HTMLElement>('.fate-tide-sheet-ability-row');
        if (!rows.length) {
            sec.style.display = '';
            return;
        }
        var anyShown = Array.from(rows).some(function (r) {
            return getComputedStyle(r).display !== 'none';
        });
        sec.style.display = anyShown ? '' : 'none';
    });
}

/**
 * `offsetParent === null` is unreliable (e.g. fixed-position panels); use layout visibility instead.
 */
function fateTideInventoryUnequippedSearchInputIsLayoutVisible(el: Element): boolean {
    if (el.nodeType !== 1) {
        return false;
    }
    var st = typeof window.getComputedStyle === 'function' ? getComputedStyle(el) : null;
    if (!st) {
        return true;
    }
    if (st.display === 'none' || st.visibility === 'hidden') {
        return false;
    }
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
}

/** Strip bogus values extensions / legacy sync may have written. */
function fateTideNormalizeInventoryUnequippedSearchInputEl(el: Element): void {
    if (!(el instanceof HTMLInputElement)) {
        return;
    }
    var v = el.value;
    if (v === 'undefined' || v === 'null') {
        el.value = '';
    }
    var phAttr = el.getAttribute('placeholder');
    if (phAttr === 'undefined' || phAttr === 'null') {
        el.removeAttribute('placeholder');
    }
    try {
        if (el.placeholder === 'undefined' || el.placeholder === 'null') {
            el.placeholder = '';
        }
    } catch (_e) {}
}

function fateTideScrubInventoryUnequippedSearchInputsInContainer(root: ParentNode): void {
    if (!root) {
        return;
    }
    root.querySelectorAll<HTMLInputElement>('input.fate-tide-inventory-unequipped-search').forEach(function (el) {
        fateTideNormalizeInventoryUnequippedSearchInputEl(el);
    });
}

function fateTideApplyInventoryUnequippedSearchInWrap(wrap: HTMLElement, sourceInput?: HTMLInputElement): void {
    if (!wrap || !wrap.classList || !wrap.classList.contains('fate-tide-inventory-unequipped-search-root')) {
        return;
    }
    var wrapId = wrap.id || '';
    var input = sourceInput || null;
    if (!input && wrapId) {
        var inputs = document.querySelectorAll<HTMLInputElement>('input.fate-tide-inventory-unequipped-search[data-ft-inventory-unequipped-root="' + wrapId + '"]');
        for (var i = 0; i < inputs.length; i++) {
            const candidate = inputs[i];
            if (candidate && fateTideInventoryUnequippedSearchInputIsLayoutVisible(candidate)) {
                input = candidate;
                break;
            }
        }
        if (!input) {
            for (var j = 0; j < inputs.length; j++) {
                var jv = inputs[j]?.value;
                if (jv && jv !== 'undefined' && jv !== 'null') {
                    input = inputs[j] ?? null;
                    break;
                }
            }
        }
        if (!input && inputs.length) {
            input = inputs[0] ?? null;
        }
    }
    if (!input) {
        input = wrap.querySelector<HTMLInputElement>('input.fate-tide-inventory-unequipped-search');
    }
    if (input) {
        fateTideNormalizeInventoryUnequippedSearchInputEl(input);
    }
    if (wrapId) {
        var linked = document.querySelectorAll<HTMLInputElement>('input.fate-tide-inventory-unequipped-search[data-ft-inventory-unequipped-root="' + wrapId + '"]');
        for (var sani = 0; sani < linked.length; sani++) {
            const linkedInput = linked[sani];
            if (linkedInput) fateTideNormalizeInventoryUnequippedSearchInputEl(linkedInput);
        }
    }
    var innerOnly = wrap.querySelector<HTMLInputElement>('input.fate-tide-inventory-unequipped-search');
    if (innerOnly) {
        fateTideNormalizeInventoryUnequippedSearchInputEl(innerOnly);
    }
    if (input && wrapId) {
        var master = String(input.value || '');
        if (master === 'undefined' || master === 'null') {
            master = '';
        }
        if (input.value !== master) {
            input.value = master;
        }
        var siblings = document.querySelectorAll<HTMLInputElement>('input.fate-tide-inventory-unequipped-search[data-ft-inventory-unequipped-root="' + wrapId + '"]');
        for (var k = 0; k < siblings.length; k++) {
            const sibling = siblings[k];
            if (sibling && sibling.value !== master) {
                sibling.value = master;
            }
        }
    }
    var q = (input && input.value ? input.value : '').toLowerCase().trim();
    // Item-type quick filter (filter-search-init type bar) — ANDed with the text search so the two
    // never clobber each other's display toggles. Empty (the default / "All") means no type constraint.
    var activeType = wrap.getAttribute('data-ft-inventory-type-filter-active') || '';
    // Hide-wares view toggle (PC-sheet header button stamps this on the search root). ANDed here —
    // NOT a separate pass — so the search/type toggles can never re-show a hidden wares tile.
    var hideWares = wrap.getAttribute('data-ft-inventory-hide-wares') === '1';

    wrap.querySelectorAll<HTMLElement>('[data-inventory-search-zone="1"] .inventory-item').forEach(function (el) {
        var matchType = !activeType || (el.getAttribute('data-item_type') || '') === activeType;
        var matchQ = !q || (el.getAttribute('data-inventory_name') || '').toLowerCase().indexOf(q) !== -1;
        var matchWares = !hideWares || el.getAttribute('data-is_wares') !== '1';
        el.style.display = (matchType && matchQ && matchWares) ? '' : 'none';
    });
}

// =====================================================================================
// INVENTORY ITEM-TYPE QUICK FILTER (icon bar) — trade/train merchant stock
// -------------------------------------------------------------------------------------
// Opt-in: a `.ft-inv-type-filter-bar[data-ft-inv-type-filter="1"]` container emitted inside the
// search root (see _trade_panel_inc.php). This builds a row of item_type icon chips from the types
// ACTUALLY present in the rendered stock tiles (data-item_type), plus an "All" chip. Clicking a chip
// sets `data-ft-inventory-type-filter-active` on the wrap; fateTideApplyInventoryUnequippedSearchInWrap
// ANDs it with the text search. The bar is rebuilt on every fateTideRefreshInventoryUnequippedSearch
// (which trade-trainer-init calls after each trade op), so it tracks stock as items change hands.
// Icons resolve through the same global iconMap() the tiles use; types iconMap can't resolve fall back
// to a text label so nothing renders blank.

function ftInventoryTypePretty(type: unknown): string {
    return String(type || '')
        .split('_')
        .map(function (w: string) { return w ? w.charAt(0).toUpperCase() + w.slice(1) : w; })
        .join(' ');
}

function ftInventoryTypeFilterEscape(value: unknown): string {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function ftInventoryTypeChipHtml(type: string, label: string, isActive: boolean): string {
    var iconHtml = '';
    if (type && typeof window.iconMap === 'function') {
        // 4th arg '' (default, NOT false) → iconMap wraps the <img> in its standard instant-Bootstrap
        // tooltip span (data-bs-toggle + data-bs-original-title from the icon registry's canonical name),
        // so hovering a chip explains the type. 30px icon; the chip is a fixed square (CSS) so the icon
        // tiles and the "All" text tile are uniform.
        iconHtml = window.iconMap(type, 30, 30, '') || '';
    }
    var inner = iconHtml || ftInventoryTypeFilterEscape(label);
    // Sizing/padding live entirely in CSS (#div_merchant_trade_panel .ft-inv-type-chip) so the icon and
    // "All" tiles share one square box — NO Bootstrap px-*/py-* here (those carry !important and would win).
    var cls = 'btn btn-sm btn-dark border ft-inv-type-chip' + (isActive ? ' active' : '');
    var aria = type ? ('Show only ' + label) : 'Show all item types';
    // No native title= on icon chips — iconMap's span carries the (instant) tooltip; a native title would
    // double up with the slow browser one. The "All" chip has visible text, so it needs no tooltip either.
    return '<button type="button" class="' + cls + '"'
        + ' data-ft-inv-type-chip="' + ftInventoryTypeFilterEscape(type) + '"'
        + ' aria-pressed="' + (isActive ? 'true' : 'false') + '"'
        + ' aria-label="' + ftInventoryTypeFilterEscape(aria) + '">'
        + inner
        + '</button>';
}

function fateTideBuildInventoryTypeFilterBar(wrap: HTMLElement): void {
    if (!wrap) {
        return;
    }
    var bar = wrap.querySelector<HTMLElement>('.ft-inv-type-filter-bar[data-ft-inv-type-filter="1"]');
    if (!bar) {
        return; // not an opted-in root (e.g. the PC's own inventory tab) — nothing to do
    }
    var zone = wrap.querySelector<HTMLElement>('[data-inventory-search-zone="1"]');
    if (!zone) {
        bar.innerHTML = '';
        return;
    }
    var tiles = zone.querySelectorAll('.inventory-item');
    // Tiles hydrate async (ftMountInventoryTiles). If only un-hydrated mounts exist, retry a bounded
    // number of animation frames so the bar populates once the tiles land — no coupling to internals.
    if (!tiles.length) {
        var pendingMounts = zone.querySelectorAll('.ft-inv-tile-mount:not([data-inv-tile-mounted])');
        if (pendingMounts.length) {
            var tries = parseInt(bar.getAttribute('data-ft-type-bar-retries') || '0', 10) || 0;
            if (tries < 30 && typeof requestAnimationFrame === 'function') {
                bar.setAttribute('data-ft-type-bar-retries', String(tries + 1));
                requestAnimationFrame(function () { fateTideBuildInventoryTypeFilterBar(wrap); });
            }
            return;
        }
    }
    bar.removeAttribute('data-ft-type-bar-retries');

    var seen: Record<string, boolean> = {};
    for (var i = 0; i < tiles.length; i++) {
        var t = (tiles[i]?.getAttribute('data-item_type') || '').trim();
        if (t) {
            seen[t] = true;
        }
    }
    var types = Object.keys(seen).sort();

    // Drop a stale active filter if that type is no longer in stock (e.g. last one was just sold).
    var active = wrap.getAttribute('data-ft-inventory-type-filter-active') || '';
    if (active && types.indexOf(active) === -1) {
        active = '';
        wrap.setAttribute('data-ft-inventory-type-filter-active', '');
    }

    // A one-type bar is just noise — only show the filter when there's an actual choice.
    if (types.length < 2) {
        bar.innerHTML = '';
        return;
    }

    var html = ftInventoryTypeChipHtml('', 'All', active === '');
    for (var k = 0; k < types.length; k++) {
        const type = types[k];
        if (type === undefined) continue;
        html += ftInventoryTypeChipHtml(type, ftInventoryTypePretty(type), active === type);
    }
    bar.innerHTML = html;
}

(function ftInstallInventoryTypeFilterDelegation() {
    if (typeof document === 'undefined' || window.__ftInvTypeFilterDelegationBound) {
        return;
    }
    window.__ftInvTypeFilterDelegationBound = true;
    document.addEventListener('click', function (e) {
        var target = e.target;
        var chip = target instanceof Element ? target.closest<HTMLElement>('[data-ft-inv-type-chip]') : null;
        if (!chip) {
            return;
        }
        var bar = chip.closest<HTMLElement>('.ft-inv-type-filter-bar[data-ft-inv-type-filter="1"]');
        if (!bar) {
            return;
        }
        var rootId = bar.getAttribute('data-ft-inventory-unequipped-root');
        var wrap = rootId
            ? document.getElementById(rootId)
            : bar.closest<HTMLElement>('.fate-tide-inventory-unequipped-search-root');
        if (!wrap) {
            return;
        }
        var type = chip.getAttribute('data-ft-inv-type-chip') || '';
        var current = wrap.getAttribute('data-ft-inventory-type-filter-active') || '';
        // Clicking the already-active type toggles it back off (→ "All").
        var next = (type && type === current) ? '' : type;
        wrap.setAttribute('data-ft-inventory-type-filter-active', next);
        bar.querySelectorAll<HTMLElement>('[data-ft-inv-type-chip]').forEach(function (c) {
            var isActive = (c.getAttribute('data-ft-inv-type-chip') || '') === next;
            c.classList.toggle('active', isActive);
            c.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        fateTideApplyInventoryUnequippedSearchInWrap(wrap);
    });
})();

type FtCampaignPlacementStart = (request: {
    encounterId: string; creatureId: string; name: string; pictureUrl: string; widthFt: number; heightFt: number;
    place: (point: { x: number; y: number; z: number }) => void; onCancel?: () => void;
}) => boolean;

/** The FT3D campaign shell's ghost placement (FT3DAOEPlacement.startCreature), from this window or, when this
 *  panel runs inside the Edit Scene frame, from its same-origin parent. null = no FT3D play surface here. */
function ftCampaignCreaturePlacementStart(): FtCampaignPlacementStart | null {
    const hosts: Window[] = [window];
    try { if (window.parent && window.parent !== window) hosts.push(window.parent); } catch { /* cross-origin */ }
    for (const host of hosts) {
        try {
            const start = (host as unknown as { ftStartCreaturePlacement?: FtCampaignPlacementStart }).ftStartCreaturePlacement;
            if (typeof start === 'function' && host.document.body.classList.contains('ft3d-campaign-play-active')) return start;
        } catch { /* cross-origin parent */ }
    }
    return null;
}

const ftAddInventorySearchGenerations = new WeakMap<HTMLElement, number>();
const ftAddInventorySearchTimers = new WeakMap<HTMLInputElement, ReturnType<typeof setTimeout>>();

async function ftRunAddInventorySearch(mount: HTMLElement): Promise<void> {
    const inputId = mount.dataset.ftSearchInputId || '';
    const targetId = mount.dataset.ftSearchDivRefresh || '';
    const typeId = mount.dataset.ftItemTypeInputId || '';
    const ownedId = mount.dataset.ftOnlyOwnedInputId || '';
    const input = inputId ? document.getElementById(inputId) as HTMLInputElement | null : null;
    const target = targetId ? document.getElementById(targetId) : null;
    if (!input || !target) return;
    const generation = (ftAddInventorySearchGenerations.get(mount) ?? 0) + 1;
    ftAddInventorySearchGenerations.set(mount, generation);
    const query = input.value;
    const isStale = () => ftAddInventorySearchGenerations.get(mount) !== generation || input.value !== query;
    if (mount.dataset.ftOwnedSettingInitialized !== '1') {
        const owned = ownedId ? document.getElementById(ownedId) as HTMLInputElement | null : null;
        let stored = localStorage.getItem('ft_hide_locked_add_inventory_search');
        if (stored !== '0' && stored !== '1') {
            stored = '1';
            localStorage.setItem('ft_hide_locked_add_inventory_search', stored);
        }
        if (owned) owned.checked = stored === '1';
        mount.dataset.ftOwnedSettingInitialized = '1';
    }
    const keyword = input.value.trim();
    if (keyword === '') {
        target.replaceChildren();
        const empty = document.createElement('div');
        empty.className = 'text-center text-muted mt-4';
        empty.textContent = 'Start typing to search for items to add to inventory.';
        target.append(empty);
        return;
    }
    const typeValue = typeId ? getElementValue(typeId) : '';
    const itemType = typeof typeValue === 'string' ? typeValue : '';
    const ownedValue = ownedId ? getElementValue(ownedId) : null;
    const hideLocked = ownedValue === '1' || ownedValue === 1 || ownedValue === true;
    // Table license: the form stamps the caster's campaign onto the mount (it arrives on the side-form
    // URL), and we hand it back so loot from a source any table member bought lists as usable. The
    // server re-checks membership. Absent (no campaign / catalog context) = personal entitlements only.
    const campaignId = mount.dataset.ftCampaignId?.trim() || '';
    const payload = { keyword, ...(itemType !== '' ? { item_type: itemType } : {}),
        ...(campaignId !== '' ? { campaign_id: campaignId } : {}),
        ...(hideLocked ? { hide_locked: '1' } : {}) };
    try {
        const projected = await searchDefinitionPages(async offset => {
            const response = await fetch(BASE_URL + 'options/api/add_inventory_search_inc.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, offset }),
            });
            if (!response.ok) throw new Error(`Search inputs unavailable (${response.status}). Please retry.`);
            return response.json();
        }, false, hideLocked, () => !isStale());
        if (isStale() || !projected) return;
        const rows = normalizeAddInventorySearchPayload(projected);
        if (rows === null) throw new Error('Search inputs incomplete. Please retry.');
        renderAddInventorySearchResults(target, rows);
    } catch (error) {
        if (isStale()) return;
        target.replaceChildren();
        target.textContent = 'Search unavailable. Please retry.';
        displayWarning(error instanceof Error ? error.message : 'Search unavailable. Please retry.');
    }
}

function ftScheduleAddInventorySearch(mount: HTMLElement, immediate: boolean): void {
    const inputId = mount.dataset.ftSearchInputId || '';
    const input = inputId ? document.getElementById(inputId) as HTMLInputElement | null : null;
    if (!input) return;
    ftAddInventorySearchGenerations.set(mount, (ftAddInventorySearchGenerations.get(mount) ?? 0) + 1);
    const prior = ftAddInventorySearchTimers.get(input);
    if (prior !== undefined) clearTimeout(prior);
    if (immediate) {
        ftAddInventorySearchTimers.delete(input);
        void ftRunAddInventorySearch(mount);
        return;
    }
    ftAddInventorySearchTimers.set(input, setTimeout(() => {
        ftAddInventorySearchTimers.delete(input);
        void ftRunAddInventorySearch(mount);
    }, 200));
}

(function ftBindSearchFamilyActions() {
    if (typeof document === 'undefined' || window.__ftAddInventorySearchBound) return;
    window.__ftAddInventorySearchBound = true;
    document.addEventListener('input', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        const mount = input.closest<HTMLElement>('[data-ft-add-inventory-search="1"]');
        if (!mount || input.id !== mount.dataset.ftSearchInputId) return;
        ftScheduleAddInventorySearch(mount, false);
    }, true);
    document.addEventListener('keydown', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || event.key !== 'Enter') return;
        const mount = input.closest<HTMLElement>('[data-ft-add-inventory-search="1"]');
        if (!mount || input.id !== mount.dataset.ftSearchInputId) return;
        event.preventDefault();
        ftScheduleAddInventorySearch(mount, true);
    }, true);
    document.addEventListener('change', (event) => {
        const el = event.target;
        if (!(el instanceof HTMLElement)) return;
        const mount = el.closest<HTMLElement>('[data-ft-add-inventory-search="1"]');
        if (!mount) return;
        if (el.id === mount.dataset.ftOnlyOwnedInputId) {
            const cb = el as HTMLInputElement;
            localStorage.setItem('ft_hide_locked_add_inventory_search', cb.checked ? '1' : '0');
        }
        if (el.id === mount.dataset.ftOnlyOwnedInputId || el.id === mount.dataset.ftItemTypeInputId) {
            ftScheduleAddInventorySearch(mount, true);
        }
    }, true);
    document.addEventListener('click', (event) => {
        const button = event.target instanceof Element
            ? event.target.closest<HTMLElement>('[data-ft-search-action]')
            : null;
        if (!button || (button instanceof HTMLButtonElement && button.disabled)) return;
        const action = button.dataset.ftSearchAction;
        const itemId = button.dataset.ftItemId || '';
        if (itemId === '') return;
        if (action === 'open_item') {
            window.openItemInfo?.(itemId);
        } else if (action === 'add_encounter_creature') {
            // Size + the row's hydrated thumbnail feed the FT3D click-to-place ghost (side-panels-init.ts).
            const thumb = button.closest('tr')?.querySelector<HTMLImageElement>('.ft-ec-picker-thumb img[src]');
            window.addEncounterCreaturesToEncounter?.(itemId, button.dataset.ftItemName || '',
                { sizeId: button.dataset.ftItemSize || '', pictureUrl: thumb?.getAttribute('src') || '' });
        } else if (action === 'add_inventory') {
            // The encounter inventory picker (encounter_inventory_inc.php) reuses this search; its "+"
            // fills the panel's #inventory_item_id and runs the encounter add, which reads the panel's
            // qty / flags / options by id.
            const encounterMount = button.closest<HTMLElement>('[data-ft-add-inventory-search="1"][data-ft-add-target="encounter"]');
            if (encounterMount) {
                const encounterIdInput = encounterMount.querySelector<HTMLInputElement>('#inventory_item_id');
                if (!encounterIdInput || typeof window.addEncounterInventory !== 'function') return;
                encounterIdInput.value = itemId;
                // FT3D (owner 2026-09-16): arm the same click-to-place ghost the creature picker uses and add
                // at the floor point the GM clicks. The placement lifecycle lives in the campaign shell, which
                // is the parent when this panel runs inside Edit Scene. Anywhere else the add goes straight in.
                const startPlacement = ftCampaignCreaturePlacementStart();
                // The ghost drops into the shell's ACTIVE scene; only arm it when that is the encounter this panel adds to.
                const panelEncounterId = String((document.getElementById('hidden_input_modify_encounter_id') as HTMLInputElement | null)?.value
                    || (typeof getSidelogContext === 'function' ? getSidelogContext()?.encounter_id : '') || '').trim().toUpperCase();
                let shellEncounterId = '';
                try { shellEncounterId = String((window.parent as Window & { ftGetActiveEncounterId?: () => string }).ftGetActiveEncounterId?.() || '').trim().toUpperCase(); } catch { /* cross-origin */ }
                if (startPlacement && panelEncounterId !== '' && (shellEncounterId === '' || shellEncounterId === panelEncounterId)) {
                    const name = button.closest('li')?.querySelector<HTMLElement>('[data-ft-search-action="open_item"]')?.textContent?.trim() || 'item';
                    const pictureUrl = button.closest('li')?.querySelector<HTMLImageElement>('.ft-thumb-mount img[src]')?.getAttribute('src') || '';
                    const armed = startPlacement({
                        encounterId: panelEncounterId, creatureId: itemId, name, pictureUrl, widthFt: 5, heightFt: 5,
                        place: point => {
                            // Re-read the picked item: another "+" may have re-filled the shared hidden id.
                            encounterIdInput.value = itemId;
                            void window.addEncounterInventory?.({ target: button } as unknown as Event, point);
                        },
                    });
                    if (armed) return;
                }
                void window.addEncounterInventory({ target: button } as unknown as Event);
                return;
            }
            const idInput = document.getElementById('input_add_inventory_item_id') as HTMLInputElement | null;
            if (!idInput || typeof window.ftAdjustForCaster !== 'function') return;
            idInput.value = itemId;
            // The form stamps the RECEIVING creature onto its mount (it rides in on the side-form URL).
            // Empty = the legacy ambient-active-caster fallback inside ftAdjustForCaster().
            const addMount = button.closest<HTMLElement>('[data-ft-add-inventory-search="1"]')
                ?? document.querySelector<HTMLElement>('[data-ft-add-inventory-search="1"]');
            const addCasterId = addMount?.dataset.ftCasterId?.trim() || '';
            void window.ftAdjustForCaster(
                BASE_URL + 'options/tools/inventory/add_caster_inventory_inc.php',
                addCasterId,
                { item_id: 'input_add_inventory_item_id', item_qty: 'input_add_inventory_qty',
                    enchantment_template: 'input_apply_inventory_enchantment_template' },
                'div_sidelog_warning',
            );
        }
    }, true);
})();

function fateTideRefreshInventoryUnequippedSearch(): void {
    document.querySelectorAll<HTMLElement>('.fate-tide-inventory-unequipped-search-root').forEach(function (wrap) {
        fateTideBuildInventoryTypeFilterBar(wrap);
        fateTideApplyInventoryUnequippedSearchInWrap(wrap);
    });
}

(function fateTideInitSpellSearchDelegation() {
    if (window.__fateTideSpellSearchInputBound) {
        return;
    }
    window.__fateTideSpellSearchInputBound = true;
    document.addEventListener('input', function (e) {
        var t = e.target;
        if (!(t instanceof HTMLInputElement)) {
            return;
        }
        if (t.id === 'input_prepared_spells_search') {
            fateTideApplyPreparedSpellFilters();
        }
        var castRoot = t.getAttribute && t.getAttribute('data-ft-castable-root');
        if (castRoot) {
            var cw = document.getElementById(castRoot);
            if (cw) {
                fateTideApplyCastableSpellFiltersInWrap(cw);
            }
        }
        var sheetRoot = t.getAttribute && t.getAttribute('data-ft-sheet-abilities-root');
        if (sheetRoot) {
            var sw = document.getElementById(sheetRoot);
            if (sw) {
                fateTideApplySheetAbilitySearchInWrap(sw);
            }
        }
        var invUnequippedRoot = t.getAttribute && t.getAttribute('data-ft-inventory-unequipped-root');
        if (invUnequippedRoot) {
            var iw = document.getElementById(invUnequippedRoot);
            if (iw) {
                fateTideApplyInventoryUnequippedSearchInWrap(iw, t);
            }
        }
    });
})();

/**
 * Narrow markup root pairing dependent dynamic selects (encounter inventory: item type ↔ object template).
 */
function ftDynamicSelectPairRoot(el: Element): Element | null {
    if (!el) {
        return null;
    }
    let r = el.closest('[data-ft-dynamic-select-root]');
    if (r) {
        return r;
    }
    r = el.closest('.inventory-container-form');
    if (r) {
        return r;
    }
    r = el.closest('form');
    return r || null;
}

/**
 * Dynamic Tom Select: optional sibling select id in data-ft-dynamic-filter-by.
 */
function ftResolveDynamicSelectFilterValue(templateSelectEl: FtFilterSelectElement | null): string {
    const id = (templateSelectEl?.dataset.ftDynamicFilterBy || '').trim();
    if (!id || typeof document === 'undefined' || !templateSelectEl) {
        return '';
    }
    let root = ftDynamicSelectPairRoot(templateSelectEl);
    let fel: FtFilterHostElement | null = null;
    try {
        fel = root ? root.querySelector<FtFilterHostElement>('#' + CSS.escape(id)) : null;
    } catch (_e) {
        fel = null;
    }
    if (!fel) {
        const pane = templateSelectEl.closest('.tab-pane');
        if (pane) {
            try {
                fel = pane.querySelector<FtFilterHostElement>('#' + CSS.escape(id));
            } catch (_e2) {
                fel = null;
            }
        }
    }
    if (!fel) {
        fel = document.getElementById(id) as FtFilterHostElement | null;
    }
    if (!fel) {
        return '';
    }
    if (fel.tomselect && typeof fel.tomselect.getValue === 'function') {
        const v = fel.tomselect.getValue();
        if (Array.isArray(v)) {
            return String(v.find(Boolean) ?? '').trim();
        }
        return String(v ?? '').trim();
    }
    return String(fel.value || '').trim();
}

/**
 * Generic dynamic-select filter refresh.
 */
function ftRefreshDynamicFilteredSelects(sourceSel: FtFilterSelectElement): void {
    if (!sourceSel || !sourceSel.id) {
        return;
    }
    const sourceRoot = ftDynamicSelectPairRoot(sourceSel) || sourceSel.closest('.tab-pane');
    const escapedId = (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        ? CSS.escape(sourceSel.id)
        : sourceSel.id.replace(/(["\\])/g, '\\$1');
    const targets = document.querySelectorAll<FtFilterSelectElement>(
        'select.dynamic-item-select[data-ft-dynamic-filter-by="' + escapedId + '"]'
    );
    targets.forEach(function (target) {
        if (!target || !target.tomselect) {
            return;
        }
        const targetRoot = ftDynamicSelectPairRoot(target) || target.closest('.tab-pane');
        if (sourceRoot && targetRoot && sourceRoot !== targetRoot) {
            return;
        }
        const ts = target.tomselect;
        // A filter narrows the DROPDOWN list — it must never delete the row's existing pick.
        // The old ts.clear(true) here wiped prefilled/user-chosen values whenever a filter was
        // applied (boot prefill microtask + user filter changes), which is how equipped-inventory
        // rows lost their items (2026-07-07). Preserve the selection across the option reload.
        const rawKeep = ts.getValue();
        const keepValues = (Array.isArray(rawKeep) ? rawKeep : [rawKeep])
            .map(function (v: unknown) { return String(v ?? '').trim(); })
            .filter(function (v: string) { return v !== ''; });
        const keepOptions = keepValues
            .map(function (v: string) { return ts.options[v]; })
            .filter(Boolean)
            .map(function (o) { return { ...o }; });
        ts.lastQuery = null;
        ts.loadedSearches = {};
        if (typeof ts.clearOptions === 'function') {
            ts.clearOptions(); // Tom Select v2 keeps selected items' options; re-add below covers older paths
        } else {
            Object.keys(ts.options).forEach(function (k) {
                if (k !== '' && keepValues.indexOf(k) === -1) {
                    ts.removeOption(k);
                }
            });
        }
        keepOptions.forEach(function (o) {
            const val = String(o.value ?? '').trim();
            if (val !== '' && !ts.options[val]) {
                ts.addOption(o);
            }
        });
        keepValues.forEach(function (v: string) {
            if (!ts.items.includes(v)) {
                ts.addItem(v, true);
            }
        });
        ts.refreshOptions(false);
        if (target.dataset.preloadFull === 'true' && typeof ts.load === 'function') {
            ts.load('');
        }
    });
}

(function ftInstallDeferTabTomSelectWarmupOnce() {
    if (typeof document === 'undefined' || window._ftDeferTabTomSelectWarmupInstalled) {
        return;
    }
    window._ftDeferTabTomSelectWarmupInstalled = true;
    document.addEventListener('shown.bs.tab', function (evt) {
        const triggerBtn = evt.target;
        if (!(triggerBtn instanceof Element)) {
            return;
        }
        let tgtSel = triggerBtn.getAttribute('data-bs-target') || '';
        if (!tgtSel) {
            const href = triggerBtn.getAttribute('href') || '';
            if (href.charAt(0) === '#') {
                tgtSel = href;
            }
        }
        if (!tgtSel || tgtSel.charAt(0) !== '#') {
            return;
        }
        let pane: Element | null = null;
        try {
            pane = document.querySelector(tgtSel);
        } catch (_e) {
            return;
        }
        if (!pane || pane.getAttribute('data-ft-ts-defer-preload-until-tab-visible') !== '1') {
            return;
        }
        queueMicrotask(function () {
            pane.querySelectorAll<FtFilterSelectElement>('select.dynamic-item-select[data-item-type]').forEach(function (nativeSel) {
                const tsInst = nativeSel.tomselect;
                if (!tsInst || typeof tsInst.load !== 'function') {
                    return;
                }
                if (nativeSel.dataset.preloadFull !== 'true') {
                    return;
                }
                try {
                    tsInst.loadedSearches = {};
                } catch (_e2) {}
                tsInst.load('');
            });
        });
    });
})();

// Cross-file callers — exposed on window so they resolve from tom-select-init.js and trade-trainer-init.js.
window.fateTideRefreshSpellTableFilters = fateTideRefreshSpellTableFilters;
window.fateTideRefreshInventoryUnequippedSearch = fateTideRefreshInventoryUnequippedSearch;
window.fateTideBuildInventoryTypeFilterBar = fateTideBuildInventoryTypeFilterBar;
window.ftResolveDynamicSelectFilterValue = ftResolveDynamicSelectFilterValue;
window.ftRefreshDynamicFilteredSelects = ftRefreshDynamicFilteredSelects;
// PARITY: implicit global consumed cross-file by trade-trainer-init.js (bare typeof-guarded calls) — MUST
// stay attached or that scrub silently no-ops. (Not in the original's window list; it relied on the
// classic-script implicit global, which a module does not provide.)
window.fateTideScrubInventoryUnequippedSearchInputsInContainer = fateTideScrubInventoryUnequippedSearchInputsInContainer;


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// CATALOG / LIBRARY SEARCH
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ftGetProductTypeIdsForLibrarySearch(productTypeFilterRootId: unknown): string[] {
    if (!productTypeFilterRootId) {
        return [];
    }
    const el = document.getElementById(String(productTypeFilterRootId)) as FtFilterHostElement | null;
    if (!el) {
        return [];
    }
    if (el.getAttribute('data-ft-catalog-product-type-filter') === '1') {
        return Array.from(el.querySelectorAll('.ft-catalog-pt-chip[aria-pressed="true"]'))
            .map(function (chip) {
                const raw = chip.getAttribute('data-product-type-id');
                const v = typeof raw === 'string' ? raw.trim() : '';
                return v;
            })
            .filter(Boolean);
    }
    if (el.tomselect) {
        const v = el.tomselect.getValue();
        if (Array.isArray(v)) {
            return v.map((item) => String(item ?? '').trim()).filter(Boolean);
        }
        return v ? [String(v)] : [];
    }
    if (!(el instanceof HTMLSelectElement)) {
        return [];
    }
    return Array.from(el.selectedOptions)
        .map(function (o) { return o.value; })
        .filter(Boolean);
}

/**
 * Escape a string for use inside a `[attr="…"]` CSS attribute selector.
 */
function ftEscapeForCssAttributeSelectorSegment(value: unknown): string {
    if (typeof value !== 'string' || value === '') {
        return '';
    }
    return (typeof CSS !== 'undefined' && typeof CSS.escape === 'function')
        ? CSS.escape(value)
        : String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Persist product type chip selections to `?product_types=` (comma list) without reload.
 */
function ftCatalogSourceListSyncProductTypesUrl(filterRootId: unknown): void {
    const ids = ftGetProductTypeIdsForLibrarySearch(filterRootId || null);
    try {
        const url = new URL(window.location.href);
        if (ids.length) {
            url.searchParams.set('product_types', ids.join(','));
            url.searchParams.delete('product_type');
        } else {
            url.searchParams.delete('product_types');
            url.searchParams.delete('product_type');
        }
        window.history.replaceState({}, '', url);
    } catch (e) {}
}

/**
 * Runs URL sync + debounced AJAX list refresh after the chip filter changes.
 */
function ftInvokeCatalogProductTypeFilterFromRoot(rootEl: HTMLElement): void {
    if (!rootEl.id) {
        return;
    }
    ftCatalogSourceListSyncProductTypesUrl(rootEl.id);
    const escId = ftEscapeForCssAttributeSelectorSegment(String(rootEl.id));
    const inlineRoot = document.querySelector(
        '.ft-inline-search-root[data-ft-inline-search="1"][data-ft-product-type-select-id="' + escId + '"]');
    if (inlineRoot && typeof ftInvokeSearchWithDelayFromRoot === 'function') {
        ftInvokeSearchWithDelayFromRoot(inlineRoot);
    }
}

(function ftInstallCatalogProductTypeChipFilterDelegation() {
    if (typeof document === 'undefined' || window.__ftCatalogPtChipFilterDelegation) {
        return;
    }
    window.__ftCatalogPtChipFilterDelegation = true;

    // Chip toggle: btn-dark border (off) ↔ btn-light (on)
    document.addEventListener('click', function (ev) {
        const evt = ev.target;
        const chip = evt instanceof Element ? evt.closest<HTMLElement>('.ft-catalog-pt-chip') : null;
        if (!chip) {
            return;
        }
        const root = chip.closest<HTMLElement>('[data-ft-catalog-product-type-filter="1"]');
        if (!root || !root.id) {
            return;
        }
        const pressed = chip.getAttribute('aria-pressed') === 'true';
        const next = !pressed;
        chip.setAttribute('aria-pressed', next ? 'true' : 'false');
        chip.classList.toggle('btn-light', next);
        chip.classList.toggle('btn-dark', !next);
        chip.classList.toggle('border', !next);
        ftInvokeCatalogProductTypeFilterFromRoot(root);
    });

    // Toolbar button color: btn-secondary (closed) ↔ btn-primary (open)
    document.addEventListener('show.bs.collapse', function (ev) {
        const collapseEl = ev.target;
        if (!(collapseEl instanceof HTMLElement) || !collapseEl.id) {
            return;
        }
        const btn = document.querySelector('[data-ft-catalog-pt-toggle][data-bs-target="#' + CSS.escape(collapseEl.id) + '"]');
        if (!btn) {
            return;
        }
        btn.classList.remove('btn-info');
        btn.classList.add('btn-success');
    });

    document.addEventListener('hide.bs.collapse', function (ev) {
        const collapseEl = ev.target;
        if (!(collapseEl instanceof HTMLElement) || !collapseEl.id) {
            return;
        }
        const btn = document.querySelector('[data-ft-catalog-pt-toggle][data-bs-target="#' + CSS.escape(collapseEl.id) + '"]');
        if (!btn) {
            return;
        }
        btn.classList.remove('btn-success');
        btn.classList.add('btn-info');
    });
})();


type SearchFamilyMode = 'php' | 'json' | 'compare';

interface SearchAction {
    kind: string;
    enabled: boolean;
    reasonCode?: string;
}

interface SearchIdentity {
    id: string;
    label: string;
    type: string;
    typeLabel: string;
    url: string;
    iconId: string;
    action: SearchAction;
}

interface CampaignSearchRow {
    id: string;
    name: string;
    url: string;
    world: { id: string; label: string };
    calendar: { id: string; label: string };
    worldDate: string;
    gm: { id: string; label: string };
    playerCount: number;
    startDate: string;
    endDate: string;
    actions: SearchAction[];
}

interface CreatureSearchRow extends SearchIdentity {
    size: string;
    creatureType: string;
    creatureSubType: string;
    creatureSubTypeLabel: string;
    cr: number | null;
    source: { id: string; label: string; url: string };
    // ft_thumbnail_payload() data (optional: older/other callers omit it → the type icon is shown).
    thumbnail: Record<string, unknown> | null;
}

interface AddInventorySearchRow extends SearchIdentity {
    // label '' = the N/A source (not shown); url '' = no source page.
    source: { id: string; label: string; url: string };
    // Locked row's way in (source page to buy, or FateTide Plus); null when there is none.
    purchase: { label: string; url: string } | null;
    // ft_thumbnail_payload() data; null → the item-type icon.
    thumbnail: Record<string, unknown> | null;
}

function ftSearchRecord(value: unknown): Record<string, unknown> | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function ftNormalizeSearchAction(value: unknown): SearchAction | null {
    const row = ftSearchRecord(value);
    if (!row || typeof row.kind !== 'string' || typeof row.enabled !== 'boolean') {
        return null;
    }
    return {
        kind: row.kind,
        enabled: row.enabled,
        ...(typeof row.reason_code === 'string' ? { reasonCode: row.reason_code } : {}),
    };
}

function ftNormalizeSearchIdentity(value: unknown): SearchIdentity | null {
    const row = ftSearchRecord(value);
    const icon = ftSearchRecord(row?.icon);
    const action = ftNormalizeSearchAction(row?.action);
    if (!row || typeof row.id !== 'string' || typeof row.label !== 'string'
        || typeof row.type !== 'string' || typeof row.type_label !== 'string'
        || typeof row.url !== 'string' || !icon || typeof icon.id !== 'string' || !action) {
        return null;
    }
    return { id: row.id, label: row.label, type: row.type, typeLabel: row.type_label,
        url: row.url, iconId: icon.id, action };
}

function ftNormalizeNamedIdentity(value: unknown): { id: string; label: string } | null {
    const row = ftSearchRecord(value);
    return row && typeof row.id === 'string' && typeof row.label === 'string'
        ? { id: row.id, label: row.label }
        : null;
}

function normalizeItemTypeSearchPayload(value: unknown, renderer: string): CampaignSearchRow[] | CreatureSearchRow[] | null {
    if (renderer === 'public_campaigns') {
        try { return campaignSearchRows(value, BASE_URL); } catch { return null; }
    }
    const payload = ftSearchRecord(value);
    if (!payload || payload.shape_v !== 1 || payload.renderer !== renderer || !Array.isArray(payload.results)) {
        return null;
    }
    if (renderer === 'encounter_creature_picker') {
        const rows: CreatureSearchRow[] = [];
        for (const raw of payload.results) {
            const row = ftSearchRecord(raw);
            const identity = ftNormalizeSearchIdentity(raw);
            const sourceBase = ftNormalizeNamedIdentity(row?.source);
            const source = ftSearchRecord(row?.source);
            if (!row || !identity || !sourceBase || !source || typeof source.url !== 'string'
                || typeof row.size !== 'string' || typeof row.creature_type !== 'string'
                || typeof row.creature_sub_type !== 'string' || typeof row.creature_sub_type_label !== 'string'
                || !(typeof row.cr === 'number' || row.cr === null)) {
                return null;
            }
            rows.push({ ...identity, size: row.size, creatureType: row.creature_type,
                creatureSubType: row.creature_sub_type, creatureSubTypeLabel: row.creature_sub_type_label,
                cr: row.cr, source: { ...sourceBase, url: source.url }, thumbnail: ftSearchRecord(row.thumbnail) });
        }
        return rows;
    }
    return null;
}

function normalizeAddInventorySearchPayload(value: unknown): AddInventorySearchRow[] | null {
    const payload = ftSearchRecord(value);
    if (!payload || payload.shape_v !== 1 || !Array.isArray(payload.results)) {
        return null;
    }
    const rows: AddInventorySearchRow[] = [];
    for (const raw of payload.results) {
        const identity = ftNormalizeSearchIdentity(raw);
        const row = ftSearchRecord(raw);
        const source = ftNormalizeNamedIdentity(row?.source);
        if (!identity || !source) {
            return null;
        }
        const sourceUrl = ftSearchRecord(row?.source)?.url;
        const purchase = ftSearchRecord(row?.purchase);
        rows.push({
            ...identity,
            source: { ...source, url: typeof sourceUrl === 'string' ? sourceUrl : '' },
            purchase: purchase && typeof purchase.label === 'string' && typeof purchase.url === 'string' && purchase.url !== ''
                ? { label: purchase.label, url: purchase.url }
                : null,
            thumbnail: ftSearchRecord(row?.thumbnail),
        });
    }
    return rows;
}

function ftSearchIcon(id: string, size = 22): HTMLElement {
    const span = document.createElement('span');
    if (typeof window.iconMap === 'function' && id !== '') {
        span.innerHTML = window.iconMap(id, size, size, false);
    }
    return span;
}

function ftSearchLink(label: string, href: string): HTMLAnchorElement {
    const link = document.createElement('a');
    link.className = 'link-primary';
    link.href = href;
    link.textContent = label;
    return link;
}

function ftSearchTable(headers: Array<string | HTMLElement>): { wrap: HTMLDivElement; body: HTMLTableSectionElement } {
    const wrap = document.createElement('div');
    wrap.className = 'table-responsive-xxl';
    const table = document.createElement('table');
    table.className = 'ft-sortable';
    const head = document.createElement('thead');
    const tr = document.createElement('tr');
    for (const header of headers) {
        const th = document.createElement('th');
        if (typeof header === 'string') th.textContent = header;
        else th.append(header);
        tr.append(th);
    }
    const body = document.createElement('tbody');
    head.append(tr);
    table.append(head, body);
    wrap.append(table);
    return { wrap, body };
}

function renderItemTypeSearchResults(target: HTMLElement, renderer: string, rows: CampaignSearchRow[] | CreatureSearchRow[]): void {
    target.removeAttribute('aria-busy');
    target.replaceChildren();
    if (rows.length === 0) {
        const empty = document.createElement('strong');
        empty.textContent = 'No Search Results Found';
        target.append(empty);
        return;
    }
    if (renderer === 'public_campaigns') {
        const table = ftSearchTable([ftSearchIcon('campaign'), ftSearchIcon('settings'), ftSearchIcon('world'),
            ftSearchIcon('calendar'), 'World Date', ftSearchIcon('gm'), 'Players', 'Start', 'End']);
        for (const row of rows as CampaignSearchRow[]) {
            const tr = document.createElement('tr');
            tr.dataset.ftCampaignId = row.id;
            const cells: [HTMLTableCellElement, HTMLTableCellElement, HTMLTableCellElement,
                HTMLTableCellElement, HTMLTableCellElement, HTMLTableCellElement,
                HTMLTableCellElement, HTMLTableCellElement, HTMLTableCellElement] = [
                document.createElement('td'), document.createElement('td'), document.createElement('td'),
                document.createElement('td'), document.createElement('td'), document.createElement('td'),
                document.createElement('td'), document.createElement('td'), document.createElement('td'),
            ];
            cells[0].append(ftSearchLink(row.name, row.url));
            const settings = row.actions.find((action) => action.kind === 'campaign_settings');
            if (settings?.enabled) {
                const link = ftSearchLink('', `${BASE_URL}campaign_settings.php?campaign_id=${encodeURIComponent(row.id)}`);
                link.className = 'btn btn-sm btn-info m-1';
                link.append(ftSearchIcon('campaign_settings'));
                cells[1].append(link);
            }
            cells[2].textContent = row.world.label;
            if (row.calendar.id !== '') {
                cells[3].append(ftSearchLink(row.calendar.label, `${BASE_URL}item.php?id=${encodeURIComponent(row.calendar.id)}`));
            } else cells[3].textContent = row.calendar.label;
            cells[4].textContent = row.worldDate;
            if (row.gm.id !== '') cells[5].append(ftSearchLink(row.gm.label, `${BASE_URL}user.php?id=${encodeURIComponent(row.gm.id)}`));
            else cells[5].textContent = row.gm.label;
            cells[6].textContent = String(row.playerCount);
            cells[7].textContent = row.startDate;
            cells[8].textContent = row.endDate;
            tr.append(...cells);
            table.body.append(tr);
        }
        target.append(table.wrap);
        return;
    }
    // Encounter-creature picker: compact rows sized for the narrow FT3D Workspace window —
    // add · real thumbnail · name (sub-type · source beneath) · size · type · CR, shared by keyword and Filter.
    const table = document.createElement('table');
    table.className = 'ft-ec-picker-table';
    const headRow = document.createElement('tr');
    const th = (content: string | HTMLElement, cls: string): HTMLTableCellElement => {
        const cell = document.createElement('th');
        cell.className = cls;
        if (typeof content === 'string') cell.textContent = content;
        else cell.append(content);
        return cell;
    };
    headRow.append(th(ftSearchIcon('add_creature', 20), 'ft-ec-picker-add'), th(ftSearchIcon('picture', 20), 'ft-ec-picker-thumb'),
        th('Name', 'ft-ec-picker-name'), th(ftSearchIcon('creature_size', 20), 'ft-ec-picker-icon'),
        th(ftSearchIcon('creature_type', 20), 'ft-ec-picker-icon'), th(ftSearchIcon('challenge_rating', 20), 'ft-ec-picker-cr'));
    const head = document.createElement('thead');
    head.append(headRow);
    const body = document.createElement('tbody');
    for (const row of rows as CreatureSearchRow[]) {
        const tr = document.createElement('tr');
        const td = (cls: string): HTMLTableCellElement => {
            const cell = document.createElement('td');
            cell.className = cls;
            return cell;
        };
        const cells = [td('ft-ec-picker-add'), td('ft-ec-picker-thumb'), td('ft-ec-picker-name'),
            td('ft-ec-picker-icon'), td('ft-ec-picker-icon'), td('ft-ec-picker-cr')] as const;
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'btn btn-primary btn-sm';
        add.dataset.ftSearchAction = 'add_encounter_creature';
        add.dataset.ftItemId = row.id;
        add.dataset.ftItemName = row.label;
        add.dataset.ftItemSize = row.size;
        add.disabled = !row.action.enabled;
        if (!row.action.enabled) add.title = 'This creature requires content you do not currently have access to.';
        add.append(ftSearchIcon('plus'));
        cells[0].append(add);
        cells[1].append(row.thumbnail ? ftThumbnailPlaceholder(row.thumbnail) : ftSearchIcon(row.iconId, 44));
        const name = document.createElement('button');
        name.type = 'button';
        // Blue link name matching the sibling `ftSearchLink` cells — NOT `.btn-link`,
        // whose theme skin forces UPPERCASE + a green gradient underline bar.
        name.className = 'link-primary border-0 bg-transparent p-0';
        name.dataset.ftSearchAction = 'open_item';
        name.dataset.ftItemId = row.id;
        name.textContent = row.label;
        cells[2].append(name);
        const sub: Node[] = [];
        if (row.creatureSubTypeLabel !== '') sub.push(document.createTextNode(row.creatureSubTypeLabel));
        if (row.source.label !== '') {
            if (sub.length) sub.push(document.createTextNode(' · '));
            sub.push(row.source.url !== '' ? ftSearchLink(row.source.label, row.source.url) : document.createTextNode(row.source.label));
        }
        if (sub.length) {
            const line = document.createElement('div');
            line.className = 'ft-ec-picker-sub';
            line.append(...sub);
            cells[2].append(line);
        }
        cells[3].append(ftSearchIcon(row.size));
        cells[4].append(ftSearchIcon(row.creatureType));
        cells[5].textContent = ftCrLabel(row.cr);
        tr.append(...cells);
        body.append(tr);
    }
    table.append(head, body);
    const wrap = document.createElement('div');
    wrap.className = 'ft-ec-picker-results';
    wrap.append(table);
    target.append(wrap);
    window.ftMountThumbnails?.(target);
}

// The placeholder ft_thumbnail_mount() emits server-side; ftMountThumbnails() does the picture selection
// + render, shared by keyword and Filter rows (initials fallback included).
function ftThumbnailPlaceholder(payload: Record<string, unknown>): HTMLElement {
    const mount = document.createElement('span');
    mount.className = 'ft-thumb-mount';
    const blob = document.createElement('script');
    blob.type = 'application/json';
    blob.className = 'ft-thumb-blob';
    blob.textContent = JSON.stringify(payload);
    mount.append(blob);
    return mount;
}

// CR as players read it: 1/8, 1/4, 1/2 rather than 0.125 / 0.25 / 0.5 (float2rat on the PHP side).
function ftCrLabel(cr: number | null): string {
    if (cr === null) return '';
    const fractions: Record<string, string> = { '0.125': '1/8', '0.25': '1/4', '0.5': '1/2' };
    return fractions[String(cr)] ?? String(cr);
}

function renderAddInventorySearchResults(target: HTMLElement, rows: AddInventorySearchRow[]): void {
    target.replaceChildren();
    if (rows.length === 0) {
        const empty = document.createElement('strong');
        empty.textContent = 'No Search Results Found';
        target.append(empty);
        return;
    }
    const list = document.createElement('ul');
    list.className = 'list-group text-left';
    for (const row of rows) {
        const li = document.createElement('li');
        li.className = 'list-group-item' + (row.action.enabled ? '' : ' ft-add-inventory-search-gated');
        const flex = document.createElement('div');
        flex.className = 'd-flex flex-row align-items-center';
        const openLink = document.createElement('a');
        openLink.target = '_blank';
        openLink.href = row.url;
        openLink.className = 'pe-2 flex-shrink-0';
        // The object's own picture when the search sent one; the item-type icon otherwise.
        openLink.append(row.thumbnail ? ftThumbnailPlaceholder(row.thumbnail) : ftSearchIcon(row.iconId, 32));
        const name = document.createElement('button');
        name.type = 'button';
        // Plain clickable name — NOT `.btn-link`, whose theme skin (10-main-head.css
        // ~10669) forces UPPERCASE + a green gradient underline bar. Mirrors the old
        // PHP `<div role="button">`.
        name.className = 'border-0 bg-transparent text-reset text-decoration-none text-start p-0' + (row.action.enabled ? '' : ' opacity-50');
        name.dataset.ftSearchAction = 'open_item';
        name.dataset.ftItemId = row.id;
        name.textContent = row.label;
        // Source only when it is a real title (the N/A source arrives with an empty label).
        const source = document.createElement('span');
        source.className = 'small text-muted ms-2';
        if (row.source.label !== '') {
            source.append(row.source.url !== '' ? ftSearchLink(row.source.label, row.source.url) : document.createTextNode(row.source.label));
            source.querySelector('a')?.setAttribute('target', '_blank');
        }
        const addWrap = document.createElement('div');
        addWrap.className = 'p-2 ms-auto d-flex align-items-center gap-2 flex-shrink-0';
        if (!row.action.enabled && row.purchase) {
            const buy = document.createElement('a');
            buy.className = 'btn btn-sm btn-warning text-dark';
            buy.href = row.purchase.url;
            buy.target = '_blank';
            buy.rel = 'noopener';
            buy.append(ftSearchIcon('add_to_cart', 18), document.createTextNode(` ${row.purchase.label}`));
            addWrap.append(buy);
        }
        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'btn btn-sm btn-primary';
        add.dataset.ftSearchAction = 'add_inventory';
        add.dataset.ftItemId = row.id;
        add.disabled = !row.action.enabled;
        if (!row.action.enabled) add.title = 'This item requires content you do not currently have access to.';
        add.append(ftSearchIcon('add_inventory'));
        addWrap.append(add);
        flex.append(openLink, name, source, addWrap);
        li.append(flex);
        list.append(li);
    }
    target.append(list);
    window.ftMountThumbnails?.(target);
}

function ftSearchFamilyMode(root: Element): SearchFamilyMode {
    const queryMode = new URLSearchParams(window.location.search).get('search_family');
    if (queryMode === 'php' || queryMode === 'compare') return queryMode;
    return root.getAttribute('data-ft-search-family') === 'json' ? 'json' : 'php';
}

const ftInlineSearchTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Latest search run per results div. Each run supersedes every earlier one — keyword edits, filter
// toggles, clearing back to the empty-state hint — because the debounce spaces requests out but does
// not order their replies; a superseded run may neither render nor start the PHP fallback.
const ftItemTypeSearchGeneration = new Map<string, number>();

function ftInvalidateCreatureFilter(event: Event): void {
    const control = event.target;
    if (!(control instanceof HTMLElement) || !control.closest('#div_add_encounter_creatures_table')
        || !CREATURE_FILTER_CONTROLS.some(id => id === control.id)) return;
    // Search already ran on input. Blurring the keyword emits change without scheduling
    // another search; invalidating here used to silently discard the in-flight result.
    if (event.type === 'change' && control.id === 'input_search_creature') return;
    const id = 'div_refresh_creature_search', target = document.getElementById(id);
    ftItemTypeSearchGeneration.set(id, (ftItemTypeSearchGeneration.get(id) ?? 0) + 1);
    if (target?.getAttribute('aria-busy') === 'true') {
        target.removeAttribute('aria-busy');
        target.textContent = 'Filters changed. Apply Filter or search again.';
    }
}

async function ftFilterEncounterCreatures(): Promise<void> {
    const divRefresh = 'div_refresh_creature_search';
    const target = document.getElementById(divRefresh);
    if (!target) return;
    const timer = ftInlineSearchTimers.get('input_search_creature');
    if (timer !== undefined) clearTimeout(timer);
    ftInlineSearchTimers.delete('input_search_creature');
    const generation = (ftItemTypeSearchGeneration.get(divRefresh) ?? 0) + 1;
    ftItemTypeSearchGeneration.set(divRefresh, generation);
    const selection = readCreatureFilterSelection(), signature = JSON.stringify(selection);
    const current = (): boolean => document.getElementById(divRefresh) === target
        && ftItemTypeSearchGeneration.get(divRefresh) === generation
        && JSON.stringify(readCreatureFilterSelection()) === signature;
    await runCreatureFilter(target, selection, current, value => {
        const rows = normalizeItemTypeSearchPayload(value, 'encounter_creature_picker');
        if (!rows) throw new Error('Filter results unavailable. Please retry.');
        renderItemTypeSearchResults(target, 'encounter_creature_picker', rows);
    });
    if (ftItemTypeSearchGeneration.get(divRefresh) === generation && !current()) {
        target.removeAttribute('aria-busy');
        target.textContent = 'Filters changed. Apply Filter or search again.';
    }
}

async function ftRunItemTypeSearch(root: Element, divRefresh: string, itemTypeSearch: string, renderer: string,
    searchInputId: string, productTypeSelectId: string | null, onlyOwnedInputId: string | null): Promise<void> {
    const input = document.getElementById(searchInputId) as HTMLInputElement | null;
    const target = document.getElementById(divRefresh);
    if (!input || !target) return;
    const generation = (ftItemTypeSearchGeneration.get(divRefresh) ?? 0) + 1;
    ftItemTypeSearchGeneration.set(divRefresh, generation);
    const keywordAtStart = input.value;
    const isStale = (): boolean => ftItemTypeSearchGeneration.get(divRefresh) !== generation || input.value !== keywordAtStart;
    // Opt-in search-first pickers (the FT3D add-creature form) show a prompt instead of fetching a
    // default page of results for an empty keyword — that page was never asked for and cost a full
    // item compile per row.
    const emptyHint = (root as HTMLElement).dataset?.ftSearchEmptyHint?.trim() || '';
    if (emptyHint !== '' && input.value.trim() === '') {
        const empty = document.createElement('div');
        empty.className = 'text-center text-muted mt-4 mb-3';
        empty.textContent = emptyHint;
        target.replaceChildren(empty);
        return;
    }
    const ftSourceEl = document.getElementById('ft_list_source_filter') as HTMLInputElement | null;
    const sourceId = ftSourceEl ? String(ftSourceEl.value || '').trim() : '';
    const productTypeIds = ftGetProductTypeIdsForLibrarySearch(productTypeSelectId || null);
    const rawOwn = onlyOwnedInputId ? getElementValue(onlyOwnedInputId) : null;
    const onlyOwned = rawOwn === '1' || rawOwn === 1 || rawOwn === true;
    // Table license: the server stamps the encounter's campaign onto the search root, and we hand it
    // back so creatures from a source any table member bought list as usable. The server re-checks
    // membership, so this is context, not a credential. Absent (a catalog page) = personal only.
    const campaignId = (root as HTMLElement).dataset?.ftCampaignId?.trim() || '';
    const payload = { item_type_search: itemTypeSearch, renderer, keyword: input.value,
        ...(sourceId !== '' ? { source_id: sourceId } : {}),
        ...(productTypeIds.length ? { product_type_ids: productTypeIds } : {}),
        ...(campaignId !== '' ? { campaign_id: campaignId } : {}),
        ...(onlyOwned ? { only_owned: '1' } : {}) };
    const useLegacy = (): void => {
        queryData(BASE_URL + 'options/search/search_item_type_inc.php', divRefresh, payload, true, true, false);
    };
    const nativeCreature = renderer === 'encounter_creature_picker';
    const nativeCampaign = renderer === 'public_campaigns';
    const mode = nativeCreature || nativeCampaign ? 'json' : ftSearchFamilyMode(root);
    if (mode === 'php') {
        useLegacy();
        return;
    }
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    if (nativeCreature) {
        target.textContent = 'Searching creatures…';
        target.setAttribute('aria-busy', 'true');
    }
    try {
        const readPage = async (offset = 0) => {
            const response = await fetch(BASE_URL + (nativeCampaign ? 'options/table_lists/table_list_campaigns_inc.php' : 'options/api/item_type_search_inc.php'), {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
                body: JSON.stringify({ ...payload, offset, ...(nativeCreature ? { projection: 'creature-search-card-v1' } : {}) }),
            });
            if (!response.ok) throw new Error(`Search inputs unavailable (${response.status}). Please retry.`);
            return response.json();
        };
        const projected = nativeCreature ? await searchDefinitionPages(readPage, true, onlyOwned, () => !isStale()) : await readPage();
        const rows = normalizeItemTypeSearchPayload(projected, renderer);
        if (isStale()) return;
        if (rows === null) throw new Error('Item-type JSON search returned an invalid payload');
        renderItemTypeSearchResults(target, renderer, rows);
        if (mode === 'compare') void fetch(BASE_URL + 'options/search/search_item_type_inc.php', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        }).catch(() => undefined);
    } catch (error) {
        if (isStale()) return;
        if (nativeCreature || nativeCampaign) {
            target.removeAttribute('aria-busy');
            target.replaceChildren();
            target.textContent = 'Search unavailable. Please retry.';
            displayWarning(error instanceof Error ? error.message : 'Search unavailable. Please retry.');
        } else {
            console.error('Item-type JSON search error; using PHP rollback:', error);
            useLegacy();
        }
    } finally {
        clearTimeout(timeout);
    }
}

// Search with delay function
function searchWithDelay(
    divRefresh: string,
    itemTypeSearch: string,
    renderer: string,
    searchInputId: string,
    productTypeSelectId: string | null,
    onlyOwnedInputId: string | null,
): void {
    const input = document.getElementById(searchInputId);
    const root = input?.closest('.ft-inline-search-root[data-ft-inline-search="1"]');
    if (!root) {
        if (renderer === 'encounter_creature_picker') {
            const target = document.getElementById(divRefresh);
            if (target) target.textContent = 'Search unavailable. Please retry.';
            displayWarning('Search inputs unavailable. Please retry.');
        }
        return;
    }
    ftItemTypeSearchGeneration.set(divRefresh, (ftItemTypeSearchGeneration.get(divRefresh) ?? 0) + 1);
    const prior = ftInlineSearchTimers.get(searchInputId);
    if (prior !== undefined) clearTimeout(prior);
    ftInlineSearchTimers.set(searchInputId, setTimeout(() => {
        ftInlineSearchTimers.delete(searchInputId);
        void ftRunItemTypeSearch(root, divRefresh, itemTypeSearch, renderer, searchInputId,
            productTypeSelectId, onlyOwnedInputId);
    }, 500));
}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Inline list / item-type search: spyglass + clear (delegated).
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

function ftSyncInlineSearchTrailing(root: Element): void {
    if (!root || root.getAttribute('data-ft-inline-search') !== '1') {
        return;
    }
    const inputId = root.getAttribute('data-ft-search-input-id');
    if (!inputId) {
        return;
    }
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    const glyph = root.querySelector('[data-ft-inline-search-role="glyph"]');
    const clearBtn = root.querySelector('[data-ft-inline-search-role="clear"]');
    if (!input || !glyph || !clearBtn) {
        return;
    }
    const hasText = (input.value || '').trim().length > 0;
    glyph.classList.toggle('d-none', hasText);
    clearBtn.classList.toggle('d-none', !hasText);
}

function ftInvokeSearchWithDelayFromRoot(root: Element): void {
    if (!root) {
        return;
    }
    const divRefresh = root.getAttribute('data-ft-search-div-refresh');
    const itemType = root.getAttribute('data-ft-item-type-search');
    const renderer = root.getAttribute('data-ft-search-renderer');
    const inputId = root.getAttribute('data-ft-search-input-id');
    const productTypeSelectId = root.getAttribute('data-ft-product-type-select-id') || '';
    const onlyOwnedInputId = root.getAttribute('data-ft-only-owned-input-id') || '';
    if (!divRefresh || !itemType || !renderer || !inputId || typeof searchWithDelay !== 'function') {
        return;
    }
    searchWithDelay(divRefresh, itemType, renderer, inputId, productTypeSelectId || null, onlyOwnedInputId || null);
}

function installFtInlineSearchTrailingSyncAll(): void {
    document.querySelectorAll<HTMLElement>('.ft-inline-search-root[data-ft-inline-search="1"]').forEach(ftSyncInlineSearchTrailing);
}

(function ftBindInlineSearchTrailingDelegation() {
    if (typeof document === 'undefined') {
        return;
    }
    if (window.__ftInlineSearchDelegationBound) {
        return;
    }
    window.__ftInlineSearchDelegationBound = true;
    document.addEventListener('input', ftInvalidateCreatureFilter, true);
    document.addEventListener('change', ftInvalidateCreatureFilter, true);
    document.addEventListener(
        'input',
        function (ev) {
            const el = ev.target;
            if (!(el instanceof HTMLInputElement) || !el.id) {
                return;
            }
            const root = el.closest('.ft-inline-search-root[data-ft-inline-search="1"]');
            if (!root || el.id !== root.getAttribute('data-ft-search-input-id')) {
                return;
            }
            ftSyncInlineSearchTrailing(root);
        },
        true,
    );
    document.addEventListener(
        'click',
        function (ev) {
            const btn = ev.target instanceof Element
                ? ev.target.closest<HTMLElement>('[data-ft-inline-search-role="clear"]')
                : null;
            if (!btn || btn.classList.contains('d-none')) {
                return;
            }
            const root = btn.closest('.ft-inline-search-root[data-ft-inline-search="1"]');
            if (!root) {
                return;
            }
            ev.preventDefault();
            const inputId = root.getAttribute('data-ft-search-input-id');
            const input = inputId ? document.getElementById(inputId) as HTMLInputElement | null : null;
            if (input) {
                input.value = '';
                input.focus();
                input.dispatchEvent(new Event('input', { bubbles: true }));
            }
            ftSyncInlineSearchTrailing(root);
            ftInvokeSearchWithDelayFromRoot(root);
        },
        true,
    );
})();

window.ftGetProductTypeIdsForLibrarySearch = ftGetProductTypeIdsForLibrarySearch;
window.ftEscapeForCssAttributeSelectorSegment = ftEscapeForCssAttributeSelectorSegment;
window.ftCatalogSourceListSyncProductTypesUrl = ftCatalogSourceListSyncProductTypesUrl;
window.ftInvokeCatalogProductTypeFilterFromRoot = ftInvokeCatalogProductTypeFilterFromRoot;
window.searchWithDelay = searchWithDelay;
window.ftSyncInlineSearchTrailing = ftSyncInlineSearchTrailing;
window.ftInvokeSearchWithDelayFromRoot = ftInvokeSearchWithDelayFromRoot;
window.installFtInlineSearchTrailingSyncAll = installFtInlineSearchTrailingSyncAll;
window.ftFilterEncounterCreatures = ftFilterEncounterCreatures;

export {
    fateTideApplyInventoryUnequippedSearchInWrap,
    fateTideBuildInventoryTypeFilterBar,
    fateTideNormalizeInventoryUnequippedSearchInputEl,
    ftEscapeForCssAttributeSelectorSegment,
    ftGetProductTypeIdsForLibrarySearch,
    ftInventoryTypeFilterEscape,
    ftInventoryTypePretty,
    ftResolveDynamicSelectFilterValue,
    normalizeAddInventorySearchPayload,
    normalizeItemTypeSearchPayload,
    renderAddInventorySearchResults,
    renderItemTypeSearchResults,
};
